import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import Select from "react-select";
import { bouncy } from "ldrs";
import { toast } from "react-toastify";
import {
  getWardsByZoneId,
  getAssetCategoryByRfidType,
  getAllCollectionTypes,
  getVehicleTypeDetailsByCollectionTypeId,
  getAllVehicleCapacityDetails,
  getAllOwnerTypes,
} from "../../Apis/rfidApi";
import RfidImage from "./utils/RfidImage";
import RfidMapView from "./RfidMapView";
import { resolveRfidImageUrl } from "./utils/rfidImageUtils";
import {
  getRfidValidationSchema,
  formatImeiInput,
  formatLocationNameInput,
  formatRfidTypeInput,
  formatInsuranceNoInput,
  formatInsuranceProviderInput,
  formatOtherNoInput,
  toRfidEditableValue,
  IMEI_MAX_LENGTH,
  OTHER_NO_MAX_LENGTH,
  isVehicleAssetType,
  getVehicleFieldErrors,
} from "./rfidFormValidation";
import { useRfidStore, DEFAULT_RFID_ZONE_ID, DEFAULT_RFID_ZONE_NAME } from "./store/useRfidStore";

bouncy.register();

const RFID_FILE_ACCEPT =
  "image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp";

const DEFAULT_MAP_CENTER = { lat: 28.613939, lng: 77.209021 };

const cancelButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #1e40af",
  background: "#ffffff",
  color: "#1e40af",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
};

const saveButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #0f172a",
  background: "#0f172a",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  minWidth: 72,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const sectionHeaderStyle = {
  backgroundColor: "rgb(235, 240, 247)",
  color: "#003366",
  padding: "8px 12px",
  borderRadius: "4px",
  fontWeight: 600,
  marginBottom: "16px",
};

const parseRfidDateForInput = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.split("T")[0];
  }

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().split("T")[0];
  }

  return "";
};

const findZoneIdByName = (zoneName, zones = []) =>
  zones
    .find((zone) => zone.zoneName?.trim() === zoneName?.trim())
    ?.zoneId?.toString() || "";

const findCircleIdByName = (wardName, wards = []) =>
  wards
    .find((ward) => ward.circleName?.trim() === wardName?.trim())
    ?.circleId?.toString() || "";

const findRfidTypeIdByName = (typeName, types = []) =>
  types
    .find((type) => type.assetTypeName?.trim() === typeName?.trim())
    ?.assetTypeId?.toString() || "";

const findCategoryIdByName = (categoryName, categories = []) =>
  categories
    .find(
      (category) =>
        category.assetCategoryName?.trim() === categoryName?.trim() ||
        category.rfidCategoryName?.trim() === categoryName?.trim()
    )
    ?.assetCategoryId?.toString() || "";

const findAssetAreaId = (installedArea, areas = []) =>
  areas
    .find(
      (area) =>
        area.label?.trim() === installedArea?.trim() ||
        area.assetAreaType?.trim() === installedArea?.trim()
    )
    ?.value?.toString() || "";

const RfidLocationSync = ({
  showModal,
  currentLocation,
  locationLoading,
  isEditMode,
  editRow,
  locationLandmark,
  setFieldValue,
}) => {
  const hasAppliedCurrentLocation = useRef(false);

  useEffect(() => {
    if (!showModal) {
      hasAppliedCurrentLocation.current = false;
      return;
    }
    if (isEditMode) return;
    if (!currentLocation || locationLoading) return;

    const hasExistingCoords =
      isEditMode &&
      editRow?.latitude != null &&
      editRow?.longitude != null &&
      editRow.latitude !== "" &&
      editRow.longitude !== "";

    if (hasExistingCoords || hasAppliedCurrentLocation.current) return;

    hasAppliedCurrentLocation.current = true;
    setFieldValue("latitude", currentLocation.lat);
    setFieldValue("longitude", currentLocation.lng);

    const googleMaps = window.google?.maps;
    if (googleMaps?.Geocoder && !locationLandmark?.trim()) {
      const geocoder = new googleMaps.Geocoder();
      geocoder.geocode(
        {
          location: {
            lat: Number(currentLocation.lat),
            lng: Number(currentLocation.lng),
          },
        },
        (results, status) => {
          if (status === "OK" && results?.[0]?.formatted_address) {
            setFieldValue(
              "locationLandmark",
              formatLocationNameInput(results[0].formatted_address)
            );
          }
        }
      );
    }
  }, [
    showModal,
    currentLocation,
    locationLoading,
    isEditMode,
    editRow,
    setFieldValue,
    locationLandmark,
  ]);

  return null;
};

const AddEditRfid = ({
  showModal,
  setShowModal,
  editRow,
  isEditMode,
  zoneData,
  defaultZoneId = DEFAULT_RFID_ZONE_ID,
}) => {
  const {
    rfidDevices,
    assetTypes,
    installedAreas,
    isSaving,
    saveRfid,
    fetchAssetTypes,
    fetchInstalledAreas,
  } = useRfidStore();
  const [wardData, setWardData] = useState([]);
  const [typeCategories, setTypeCategories] = useState([]);
  const [collectionTypes, setCollectionTypes] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleCapacities, setVehicleCapacities] = useState([]);
  const [ownerTypes, setOwnerTypes] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingWards, setLoadingWards] = useState(false);
  const [loadingVehicleTypes, setLoadingVehicleTypes] = useState(false);
  const [loadingVehicleCapacities, setLoadingVehicleCapacities] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);

  useEffect(() => {
    if (showModal) {
      fetchAssetTypes();
      fetchInstalledAreas();
      fetchCollectionTypes();
      fetchOwnerTypes();
    } else {
      setTypeCategories([]);
      setCollectionTypes([]);
      setVehicleTypes([]);
      setVehicleCapacities([]);
      setOwnerTypes([]);
    }
  }, [showModal, fetchAssetTypes, fetchInstalledAreas]);

  const fetchCollectionTypes = async () => {
    try {
      const response = await getAllCollectionTypes({ isActive: true });
      setCollectionTypes(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching collection types:", error);
      setCollectionTypes([]);
    }
  };

  const fetchOwnerTypes = async () => {
    try {
      const response = await getAllOwnerTypes({ isActive: true });
      setOwnerTypes(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching owner types:", error);
      setOwnerTypes([]);
    }
  };

  const fetchVehicleTypesByCollectionType = async (collectionTypeId) => {
    if (!collectionTypeId) {
      setVehicleTypes([]);
      return;
    }
    setLoadingVehicleTypes(true);
    try {
      const response = await getVehicleTypeDetailsByCollectionTypeId(
        collectionTypeId
      );
      setVehicleTypes(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching vehicle types:", error);
      setVehicleTypes([]);
    } finally {
      setLoadingVehicleTypes(false);
    }
  };

  const fetchVehicleCapacitiesByType = async (vehicleTypeId) => {
    if (!vehicleTypeId) {
      setVehicleCapacities([]);
      return;
    }
    setLoadingVehicleCapacities(true);
    try {
      const response = await getAllVehicleCapacityDetails({
        isActive: true,
        vehicleTypeId,
      });
      setVehicleCapacities(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching vehicle capacities:", error);
      setVehicleCapacities([]);
    } finally {
      setLoadingVehicleCapacities(false);
    }
  };

  const fetchCategoriesByType = async (rfidTypeId) => {
    if (!rfidTypeId) {
      setTypeCategories([]);
      return;
    }
    setLoadingCategories(true);
    try {
      const response = await getAssetCategoryByRfidType(rfidTypeId);
      setTypeCategories(response);
    } catch (error) {
      console.error("Error fetching RFID categories:", error);
      setTypeCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchWardsByZone = async (zoneId) => {
    if (!zoneId) {
      setWardData([]);
      return;
    }
    setLoadingWards(true);
    try {
      const response = await getWardsByZoneId(zoneId);
      setWardData(response.filter((circle) => circle.isActive));
    } catch (error) {
      console.error("Error fetching wards:", error);
      setWardData([]);
    } finally {
      setLoadingWards(false);
    }
  };

  useEffect(() => {
    if (!showModal) {
      setCurrentLocation(null);
      setLocationLoading(false);
      return;
    }

    if (isEditMode) {
      setLocationLoading(false);
      return;
    }

    setLocationLoading(true);
    if (!navigator.geolocation) {
      setCurrentLocation({
        lat: DEFAULT_MAP_CENTER.lat.toFixed(6),
        lng: DEFAULT_MAP_CENTER.lng.toFixed(6),
      });
      setLocationLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude.toFixed(6),
          lng: position.coords.longitude.toFixed(6),
        });
        setLocationLoading(false);
      },
      () => {
        setCurrentLocation({
          lat: DEFAULT_MAP_CENTER.lat.toFixed(6),
          lng: DEFAULT_MAP_CENTER.lng.toFixed(6),
        });
        setLocationLoading(false);
        toast.info("Unable to detect current location. Default map location is shown.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [showModal, isEditMode]);

  const resolvedDefaultZoneId = useMemo(() => {
    if (!zoneData?.length) return String(defaultZoneId || DEFAULT_RFID_ZONE_ID);
    const defaultZone =
      zoneData.find((zone) => zone.zoneName === DEFAULT_RFID_ZONE_NAME) ||
      zoneData.find(
        (zone) => String(zone.zoneId) === String(defaultZoneId || DEFAULT_RFID_ZONE_ID)
      );
    return String(defaultZone?.zoneId ?? defaultZoneId ?? DEFAULT_RFID_ZONE_ID);
  }, [zoneData, defaultZoneId]);

  const resolvedZoneId = useMemo(() => {
    if (!isEditMode || !editRow) return "";
    return (
      editRow.zoneId?.toString() ||
      findZoneIdByName(editRow.zoneName, zoneData) ||
      ""
    );
  }, [isEditMode, editRow, zoneData]);

  useEffect(() => {
    if (showModal && isEditMode && resolvedZoneId) {
      fetchWardsByZone(resolvedZoneId);
    }
    if (showModal && !isEditMode) {
      fetchWardsByZone(resolvedDefaultZoneId);
    }
  }, [showModal, isEditMode, resolvedZoneId, resolvedDefaultZoneId, editRow?.rfidMasterId]);

  const handleClose = () => {
    setShowModal(false);
    setWardData([]);
    setTypeCategories([]);
    setCollectionTypes([]);
    setVehicleTypes([]);
    setVehicleCapacities([]);
    setOwnerTypes([]);
    setCurrentLocation(null);
    setLocationLoading(false);
  };

  const resolvedRfidTypeId = useMemo(() => {
    if (!isEditMode || !editRow) return "";
    return (
      editRow.rfidTypeId?.toString() ||
      findRfidTypeIdByName(editRow.rfidTypeName, assetTypes) ||
      ""
    );
  }, [isEditMode, editRow, assetTypes]);

  useEffect(() => {
    if (showModal && isEditMode && resolvedRfidTypeId) {
      fetchCategoriesByType(resolvedRfidTypeId);
    }
  }, [showModal, isEditMode, resolvedRfidTypeId, editRow?.rfidMasterId]);

  useEffect(() => {
    if (!showModal || !isEditMode || !editRow) return;
    if (!isVehicleAssetType(resolvedRfidTypeId, assetTypes)) return;

    const collectionTypeId =
      editRow.collectionTypeId?.toString() ||
      editRow.CollectionTypeId?.toString() ||
      "";
    const vehicleTypeId =
      editRow.vehicleTypeId?.toString() ||
      editRow.VehicleTypeId?.toString() ||
      "";

    if (collectionTypeId) {
      fetchVehicleTypesByCollectionType(collectionTypeId);
    }
    if (vehicleTypeId) {
      fetchVehicleCapacitiesByType(vehicleTypeId);
    }
  }, [showModal, isEditMode, editRow, resolvedRfidTypeId, assetTypes]);

  const initialValues = useMemo(() => {
    const wardName = editRow?.wardNo ?? editRow?.wardName ?? "";
    const zoneId = isEditMode
      ? editRow?.zoneId?.toString() || findZoneIdByName(editRow?.zoneName, zoneData) || ""
      : resolvedDefaultZoneId;
    const circleId = isEditMode
      ? editRow?.circleId?.toString() || findCircleIdByName(wardName, wardData) || ""
      : "";
    const assetCategoryId = isEditMode
      ? editRow?.assetCategoryId?.toString() ||
        findCategoryIdByName(editRow?.rfidCategoryName, typeCategories) ||
        ""
      : "";
    const assetAreaId = isEditMode
      ? editRow?.assetAreaId?.toString() ||
        findAssetAreaId(editRow?.installedArea, installedAreas) ||
        ""
      : "";

    return {
      assetCategoryId,
      // rfidDeviceName: isEditMode ? editRow?.rfidDeviceName || "" : "",
      // Same rules as Add: don't prefill list placeholders ("N/A" / "-") into inputs.
      imeiSlMfgNo: isEditMode
        ? formatImeiInput(toRfidEditableValue(editRow?.imeiSlMfgNo))
        : "",
      locationLandmark: isEditMode ? editRow?.locationLandmark || "" : "",
      zoneId,
      circleId,
      assetAreaId,
      dateOfInstallation: isEditMode
        ? parseRfidDateForInput(editRow?.dateOfInstallation) || ""
        : "",
      otherNo: isEditMode
        ? formatOtherNoInput(toRfidEditableValue(editRow?.otherNo))
        : "",
      collectionTypeId: isEditMode
        ? editRow?.collectionTypeId?.toString() ||
          editRow?.CollectionTypeId?.toString() ||
          ""
        : "",
      vehicleTypeId: isEditMode
        ? editRow?.vehicleTypeId?.toString() ||
          editRow?.VehicleTypeId?.toString() ||
          ""
        : "",
      vehicleCapacityId: isEditMode
        ? editRow?.vehicleCapacityId?.toString() ||
          editRow?.VehicleCapacityId?.toString() ||
          ""
        : "",
      ownerTypeId: isEditMode
        ? editRow?.ownerTypeId?.toString() ||
          editRow?.OwnerTypeId?.toString() ||
          ""
        : "",
      rfidTypeId: isEditMode ? resolvedRfidTypeId : "",
      warrantyExpireDate: isEditMode
        ? parseRfidDateForInput(editRow?.warrantyExpireDate) || ""
        : "",
      purchaseDate: isEditMode
        ? parseRfidDateForInput(editRow?.purchaseDate) || ""
        : "",
      insuranceNo: isEditMode ? editRow?.insuranceNo || "" : "",
      insuranceValidity: isEditMode
        ? parseRfidDateForInput(editRow?.insuranceValidity) || ""
        : "",
      insuranceProvider: isEditMode ? editRow?.insuranceProvider || "" : "",
      latitude: isEditMode
        ? editRow?.latitude?.toString() || ""
        : currentLocation?.lat?.toString() || "",
      longitude: isEditMode
        ? editRow?.longitude?.toString() || ""
        : currentLocation?.lng?.toString() || "",
      installationRemarks: isEditMode ? editRow?.installationRemarks || "" : "",
      isActive: isEditMode ? Boolean(editRow?.isActive ?? true) : true,
      imageFile: null,
    };
  }, [
    isEditMode,
    editRow,
    zoneData,
    wardData,
    typeCategories,
    resolvedRfidTypeId,
    installedAreas,
    currentLocation,
    resolvedDefaultZoneId,
  ]);

  const validationSchema = useMemo(
    () =>
      getRfidValidationSchema({
        wardData,
        rfidDevices,
        assetTypes,
        isEditMode,
        editRow,
      }),
    [wardData, rfidDevices, assetTypes, isEditMode, editRow]
  );

  const validateForm = (values) => getVehicleFieldErrors(values, assetTypes);

  const handleSubmit = async (
    values,
    { setFieldError, setFieldTouched, setSubmitting }
  ) => {
    const requestPayload = {
      assetCategoryId: Number(values.assetCategoryId),
      // rfidDeviceName: values.rfidDeviceName.trim(),
      imeiSlMfgNo: values.imeiSlMfgNo.trim(),
      locationLandmark: values.locationLandmark.trim(),
      zoneId: Number(values.zoneId),
      circleId: Number(values.circleId),
      assetAreaId: Number(values.assetAreaId),
      dateOfInstallation: values.dateOfInstallation,
      otherNo: values.otherNo.trim(),
      collectionTypeId: values.collectionTypeId,
      vehicleTypeId: isVehicleAssetType(values.rfidTypeId, assetTypes)
        ? values.vehicleTypeId
        : "",
      vehicleCapacityId: isVehicleAssetType(values.rfidTypeId, assetTypes)
        ? values.vehicleCapacityId
        : "",
      ownerTypeId: isVehicleAssetType(values.rfidTypeId, assetTypes)
        ? values.ownerTypeId
        : "",
      rfidTypeId: values.rfidTypeId,
      warrantyExpireDate: values.warrantyExpireDate,
      purchaseDate: values.purchaseDate,
      insuranceNo: values.insuranceNo.trim(),
      insuranceValidity: values.insuranceValidity,
      insuranceProvider: values.insuranceProvider.trim(),
      latitude: values.latitude,
      longitude: values.longitude,
      installationRemarks: values.installationRemarks.trim(),
      isActive: Boolean(values.isActive),
      imageFile: values.imageFile,
    };

    // if (isEditMode) {
    //   requestPayload.rfidMasterId =
    //     editRow.rfidMasterId ?? editRow.assetRfidId;
    //   requestPayload.imgUrl = editRow?.imageUrl ?? editRow?.imgUrl ?? "";
    // }

    if (isEditMode) {
      requestPayload.rfidMasterId =
        editRow.rfidMasterId ?? editRow.assetRfidId;
      requestPayload.imgUrl = resolveRfidImageUrl(
        editRow?.imageUrl ?? editRow?.imgUrl ?? ""
      );
    }

    const result = await saveRfid(requestPayload, isEditMode);
    if (result?.success !== false) {
      handleClose();
      return;
    }

    if (result?.fieldErrors) {
      Object.entries(result.fieldErrors).forEach(([field, message]) => {
        if (message) {
          setFieldError(field, message);
          setFieldTouched(field, true, false);
        }
      });
    }

    setSubmitting(false);
  };

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      size="xl"
      aria-labelledby="rfid-modal-title"
      centered
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title id="rfid-modal-title">
          {isEditMode ? "Edit RFID" : "Add RFID"}
        </Modal.Title>
        <i
          className="fa-solid fa-xmark close-container"
          onClick={handleClose}
        ></i>
      </Modal.Header>
      <hr />
      <Modal.Body>
        <Formik
          key={isEditMode ? editRow?.rfidMasterId ?? editRow?.assetRfidId : "new"}
          initialValues={initialValues}
          validationSchema={validationSchema}
          validate={validateForm}
          onSubmit={handleSubmit}
          enableReinitialize={isEditMode}
        >
          {({ setFieldValue, setFieldTouched, values, errors, touched, submitCount }) => (
            <Form>
              <RfidLocationSync
                showModal={showModal}
                currentLocation={currentLocation}
                locationLoading={locationLoading}
                isEditMode={isEditMode}
                editRow={editRow}
                locationLandmark={values.locationLandmark}
                setFieldValue={setFieldValue}
              />
              <h6 className="mb-0" style={sectionHeaderStyle}>
                RFID Information
              </h6>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Asset Type <span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <Select
                    inputId="rfidTypeId"
                    name="rfidTypeId"
                    classNamePrefix="react-select"
                    options={assetTypes.map((type) => ({
                      value: String(type.assetTypeId),
                      label: type.assetTypeName,
                    }))}
                    value={
                      assetTypes
                        .map((type) => ({
                          value: String(type.assetTypeId),
                          label: type.assetTypeName,
                        }))
                        .find(
                          (option) => option.value === String(values.rfidTypeId)
                        ) || null
                    }
                    onChange={(selected) => {
                      const rfidTypeId = selected ? selected.value : "";
                      // Trigger validation so mandatory errors clear immediately.
                      setFieldValue("rfidTypeId", rfidTypeId, true);
                      setFieldValue("assetCategoryId", "", false);
                      if (!isVehicleAssetType(rfidTypeId, assetTypes)) {
                        setFieldValue("vehicleTypeId", "", false);
                        setFieldValue("vehicleCapacityId", "", false);
                        setFieldValue("ownerTypeId", "", false);
                        setVehicleTypes([]);
                        setVehicleCapacities([]);
                      } else if (values.collectionTypeId) {
                        fetchVehicleTypesByCollectionType(values.collectionTypeId);
                      }
                      fetchCategoriesByType(rfidTypeId);
                    }}
                    placeholder="Select Asset Type"
                    isClearable={false}
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                  <ErrorMessage
                    name="rfidTypeId"
                    component="div"
                    className="required-message"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Asset Category <span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <Select
                    inputId="assetCategoryId"
                    name="assetCategoryId"
                    classNamePrefix="react-select"
                    options={typeCategories.map((category) => ({
                      value: String(category.assetCategoryId),
                      label: category.assetCategoryName,
                    }))}
                    value={
                      typeCategories
                        .map((category) => ({
                          value: String(category.assetCategoryId),
                          label: category.assetCategoryName,
                        }))
                        .find(
                          (option) =>
                            option.value === String(values.assetCategoryId)
                        ) || null
                    }
                    onChange={(selected) =>
                      setFieldValue(
                        "assetCategoryId",
                        selected ? selected.value : "",
                        true
                      )
                    }
                    isLoading={loadingCategories}
                    isDisabled={loadingCategories}
                    placeholder={
                      loadingCategories
                        ? "Loading asset categories..."
                        : "Select Asset Category"
                    }
                    isClearable={false}
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                  {(submitCount > 0 || touched.assetCategoryId) &&
                    errors.assetCategoryId && (
                      <div className="required-message">
                        {errors.assetCategoryId}
                      </div>
                    )}
                </div>
                {/* <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Device Name <span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <Field
                    name="rfidDeviceName"
                    className="form-control"
                    placeholder="Enter Device Name"
                    maxLength={100}
                    onChange={(e) =>
                      setFieldValue("rfidDeviceName", formatRfidTypeInput(e.target.value))
                    }
                  />
                  <ErrorMessage
                    name="rfidDeviceName"
                    component="div"
                    className="required-message"
                  />
                </div> */}
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    IMEI/SL/MFG.NO<span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <Field
                    name="imeiSlMfgNo"
                    className="form-control"
                    placeholder="Enter IMEI/SL/MFG.NO"
                    maxLength={IMEI_MAX_LENGTH}
                    inputMode="text"
                    onChange={(e) =>
                      setFieldValue(
                        "imeiSlMfgNo",
                        formatImeiInput(e.target.value),
                        true
                      )
                    }
                  />
                  <ErrorMessage
                    name="imeiSlMfgNo"
                    component="div"
                    className="required-message"
                  />
                </div>
                {isEditMode && (
                    <div className="col-md-4 mb-3">
                      <label className="form-label">ASSET Code</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editRow?.rfidCode || editRow?.assetCode || ""}
                        disabled
                        readOnly
                      />
                    </div>
                  )}
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Location/Landmark<span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <Field
                    name="locationLandmark"
                    className="form-control"
                    placeholder="Enter Location/Landmark"
                    maxLength={100}
                    onChange={(e) =>
                      setFieldValue(
                        "locationLandmark",
                        formatLocationNameInput(e.target.value)
                      )
                    }
                  />
                  <ErrorMessage
                    name="locationLandmark"
                    component="div"
                    className="required-message"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Zone <span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <Field
                    as="select"
                    name="zoneId"
                    className="form-select"
                    onChange={(e) => {
                      const zoneId = e.target.value;
                      setFieldValue("zoneId", zoneId);
                      setFieldValue("circleId", "");
                      fetchWardsByZone(zoneId);
                    }}
                  >
                    {zoneData?.length > 0 ? (
                      zoneData.map((zone) => (
                        <option key={zone.zoneId} value={String(zone.zoneId)}>
                          {zone.zoneName}
                        </option>
                      ))
                    ) : (
                      <option value={resolvedDefaultZoneId}>
                        {DEFAULT_RFID_ZONE_NAME}
                      </option>
                    )}
                  </Field>
                  <ErrorMessage
                    name="zoneId"
                    component="div"
                    className="required-message"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Ward <span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <Select
                    inputId="circleId"
                    name="circleId"
                    classNamePrefix="react-select"
                    options={wardData.map((ward) => ({
                      value: String(ward.circleId),
                      label: ward.circleName,
                    }))}
                    value={
                      wardData
                        .map((ward) => ({
                          value: String(ward.circleId),
                          label: ward.circleName,
                        }))
                        .find(
                          (option) => option.value === String(values.circleId)
                        ) || null
                    }
                    onChange={(selected) =>
                      setFieldValue("circleId", selected ? selected.value : "")
                    }
                    isLoading={loadingWards}
                    isDisabled={loadingWards}
                    placeholder={loadingWards ? "Loading wards..." : "Select Ward"}
                    isClearable={false}
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                  <ErrorMessage
                    name="circleId"
                    component="div"
                    className="required-message"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Installed Area <span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <Select
                    inputId="assetAreaId"
                    name="assetAreaId"
                    classNamePrefix="react-select"
                    options={installedAreas.map((area) => ({
                      value: String(area.value),
                      label: area.label,
                    }))}
                    value={
                      installedAreas
                        .map((area) => ({
                          value: String(area.value),
                          label: area.label,
                        }))
                        .find(
                          (option) => option.value === String(values.assetAreaId)
                        ) || null
                    }
                    onChange={(selected) =>
                      setFieldValue("assetAreaId", selected ? selected.value : "")
                    }
                    placeholder="Select Installed Area"
                    isClearable={false}
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                  <ErrorMessage
                    name="assetAreaId"
                    component="div"
                    className="required-message"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Date Of Installation <span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <Field
                    type="date"
                    name="dateOfInstallation"
                    className="form-control"
                    max={new Date().toISOString().split("T")[0]}
                  />
                  <ErrorMessage
                    name="dateOfInstallation"
                    component="div"
                    className="required-message"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Other No/Vehicle No<span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <Field
                    name="otherNo"
                    className="form-control"
                    placeholder="Enter Other No"
                    maxLength={OTHER_NO_MAX_LENGTH}
                    onChange={(e) =>
                      setFieldValue(
                        "otherNo",
                        formatOtherNoInput(e.target.value),
                        true
                      )
                    }
                  />
                  <ErrorMessage
                    name="otherNo"
                    component="div"
                    className="required-message"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Collection Type <span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <Field
                    as="select"
                    name="collectionTypeId"
                    className="form-select"
                    onChange={(e) => {
                      const collectionTypeId = e.target.value;
                      setFieldValue("collectionTypeId", collectionTypeId, true);
                      setFieldValue("vehicleTypeId", "", false);
                      setFieldValue("vehicleCapacityId", "", false);
                      setVehicleCapacities([]);
                      if (isVehicleAssetType(values.rfidTypeId, assetTypes)) {
                        fetchVehicleTypesByCollectionType(collectionTypeId);
                      } else {
                        setVehicleTypes([]);
                      }
                    }}
                  >
                    <option value="">Select Collection Type</option>
                    {collectionTypes
                      .filter((type) => type.isActive !== false)
                      .map((type) => (
                        <option
                          key={type.collectionTypeId}
                          value={type.collectionTypeId}
                        >
                          {type.collectionTypeName}
                        </option>
                      ))}
                  </Field>
                  <ErrorMessage
                    name="collectionTypeId"
                    component="div"
                    className="required-message"
                  />
                </div>
                {isVehicleAssetType(values.rfidTypeId, assetTypes) && (
                  <>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        Vehicle Type <span style={{ color: "#EC1117" }}> *</span>
                      </label>
                      <Select
                        inputId="vehicleTypeId"
                        name="vehicleTypeId"
                        classNamePrefix="react-select"
                        options={vehicleTypes.map((type) => ({
                          value: String(type.vehicleTypeId),
                          label: type.vehicleTypeName,
                        }))}
                        value={
                          vehicleTypes
                            .map((type) => ({
                              value: String(type.vehicleTypeId),
                              label: type.vehicleTypeName,
                            }))
                            .find(
                              (option) =>
                                option.value === String(values.vehicleTypeId)
                            ) || null
                        }
                        onChange={(selected) => {
                          const vehicleTypeId = selected ? selected.value : "";
                          // Trigger validation so mandatory errors clear immediately.
                          setFieldValue("vehicleTypeId", vehicleTypeId, true);
                          setFieldValue("vehicleCapacityId", "", false);
                          fetchVehicleCapacitiesByType(vehicleTypeId);
                        }}
                        isLoading={loadingVehicleTypes}
                        isDisabled={loadingVehicleTypes}
                        placeholder={
                          loadingVehicleTypes
                            ? "Loading vehicle types..."
                            : "Select Vehicle Type"
                        }
                        isClearable={false}
                        menuPlacement="auto"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                      {(submitCount > 0 || touched.vehicleTypeId) &&
                        errors.vehicleTypeId && (
                          <div className="required-message">
                            {errors.vehicleTypeId}
                          </div>
                        )}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        Vehicle Capacity <span style={{ color: "#EC1117" }}> *</span>
                      </label>
                      <Select
                        inputId="vehicleCapacityId"
                        name="vehicleCapacityId"
                        classNamePrefix="react-select"
                        options={vehicleCapacities.map((capacity) => ({
                          value: String(capacity.vehicleCapacityId),
                          label: capacity.vehicleCapacityName,
                        }))}
                        value={
                          vehicleCapacities
                            .map((capacity) => ({
                              value: String(capacity.vehicleCapacityId),
                              label: capacity.vehicleCapacityName,
                            }))
                            .find(
                              (option) =>
                                option.value === String(values.vehicleCapacityId)
                            ) || null
                        }
                        onChange={(selected) =>
                          setFieldValue(
                            "vehicleCapacityId",
                            selected ? selected.value : ""
                          )
                        }
                        isLoading={loadingVehicleCapacities}
                        isDisabled={loadingVehicleCapacities}
                        placeholder={
                          loadingVehicleCapacities
                            ? "Loading vehicle capacities..."
                            : "Select Vehicle Capacity"
                        }
                        isClearable={false}
                        menuPlacement="auto"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                      {(submitCount > 0 || touched.vehicleCapacityId) &&
                        errors.vehicleCapacityId && (
                          <div className="required-message">
                            {errors.vehicleCapacityId}
                          </div>
                        )}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        Owner Type <span style={{ color: "#EC1117" }}> *</span>
                      </label>
                      <Select
                        inputId="ownerTypeId"
                        name="ownerTypeId"
                        classNamePrefix="react-select"
                        options={ownerTypes
                          .filter((type) => type.isActive !== false)
                          .map((type) => ({
                            value: String(type.ownerTypeId),
                            label: type.ownerTypeName,
                          }))}
                        value={
                          ownerTypes
                            .filter((type) => type.isActive !== false)
                            .map((type) => ({
                              value: String(type.ownerTypeId),
                              label: type.ownerTypeName,
                            }))
                            .find(
                              (option) =>
                                option.value === String(values.ownerTypeId)
                            ) || null
                        }
                        onChange={(selected) =>
                          setFieldValue("ownerTypeId", selected ? selected.value : "")
                        }
                        placeholder="Select Owner Type"
                        isClearable={false}
                        menuPlacement="auto"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                      {(submitCount > 0 || touched.ownerTypeId) &&
                        errors.ownerTypeId && (
                          <div className="required-message">
                            {errors.ownerTypeId}
                          </div>
                        )}
                    </div>
                  </>
                )}
                <div className="col-md-4 mb-3">
                  <label className="form-label">Warranty Expire Date</label>
                  <Field
                    type="date"
                    name="warrantyExpireDate"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="warrantyExpireDate"
                    component="div"
                    className="required-message"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Purchase Date</label>
                  <Field
                    type="date"
                    name="purchaseDate"
                    className="form-control"
                    max={new Date().toISOString().split("T")[0]}
                  />
                  <ErrorMessage
                    name="purchaseDate"
                    component="div"
                    className="required-message"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Insurance No</label>
                  <Field
                    name="insuranceNo"
                    className="form-control"
                    placeholder="Enter Insurance No"
                    maxLength={30}
                    onChange={(e) =>
                      setFieldValue("insuranceNo", formatInsuranceNoInput(e.target.value))
                    }
                  />
                  <ErrorMessage
                    name="insuranceNo"
                    component="div"
                    className="required-message"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Insurance Validity</label>
                  <Field
                    type="date"
                    name="insuranceValidity"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="insuranceValidity"
                    component="div"
                    className="required-message"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Insurance Provider</label>
                  <Field
                    name="insuranceProvider"
                    className="form-control"
                    placeholder="Enter Insurance Provider"
                    maxLength={100}
                    onChange={(e) =>
                      setFieldValue(
                        "insuranceProvider",
                        formatInsuranceProviderInput(e.target.value)
                      )
                    }
                  />
                  <ErrorMessage
                    name="insuranceProvider"
                    component="div"
                    className="required-message"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Latitude <span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <Field
                    name="latitude"
                    className="form-control"
                    placeholder="Enter Latitude"
                  />
                  <ErrorMessage
                    name="latitude"
                    component="div"
                    className="required-message"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Longitude <span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <Field
                    name="longitude"
                    className="form-control"
                    placeholder="Enter Longitude"
                  />
                  <ErrorMessage
                    name="longitude"
                    component="div"
                    className="required-message"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Upload File</label>
                  <input
                    type="file"
                    className="form-control"
                    accept={RFID_FILE_ACCEPT}
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setFieldValue("imageFile", file);
                      setFieldTouched("imageFile", true, false);
                    }}
                  />
                  <ErrorMessage
                    name="imageFile"
                    component="div"
                    className="required-message"
                  />
                  {(values.imageFile ||
                    (isEditMode && (editRow?.imageUrl ?? editRow?.imgUrl))) && (
                    <div className="mt-2">
                      <RfidImage
                        imageUrl={
                          isEditMode
                            ? editRow?.imageUrl ?? editRow?.imgUrl
                            : null
                        }
                        file={values.imageFile}
                        style={{
                          maxHeight: 80,
                          maxWidth: "100%",
                          borderRadius: 4,
                          border: "1px solid #E0E0E0",
                          objectFit: "cover",
                        }}
                        fallback={
                          <span className="text-muted small">
                            Image unavailable
                          </span>
                        }
                      />
                    </div>
                  )}
                </div>
                <div className="col-md-12 mb-3">
                  <label className="form-label">Installation Remarks</label>
                  <Field
                    as="textarea"
                    rows={3}
                    name="installationRemarks"
                    className="form-control"
                    placeholder="Enter Installation Remarks"
                  />
                  <ErrorMessage
                    name="installationRemarks"
                    component="div"
                    className="required-message"
                  />
                </div>
                <div className="col-md-12 mb-3">
                  <label className="form-label">Status</label>
                  <div className="d-flex align-items-center gap-4 mt-1">
                    <div className="form-check mb-0">
                      <input
                        type="radio"
                        className="form-check-input"
                        name="isActive"
                        id="rfidStatusActive"
                        checked={values.isActive === true}
                        onChange={() => setFieldValue("isActive", true)}
                      />
                      <label className="form-check-label" htmlFor="rfidStatusActive">
                        Active
                      </label>
                    </div>
                    <div className="form-check mb-0">
                      <input
                        type="radio"
                        className="form-check-input"
                        name="isActive"
                        id="rfidStatusInactive"
                        checked={values.isActive === false}
                        onChange={() => setFieldValue("isActive", false)}
                      />
                      <label className="form-check-label" htmlFor="rfidStatusInactive">
                        Inactive
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-2 mt-3">
                <h6 className="mb-0" style={sectionHeaderStyle}>
                  Map View
                </h6>
              </div>
              <RfidMapView
                latitude={values.latitude}
                longitude={values.longitude}
                // deviceName={values.rfidDeviceName}
                
                locationLandmark={values.locationLandmark}
                height="320px"
                showDefaultMap
                defaultCenter={
                  values.latitude && values.longitude
                    ? {
                        lat: Number(values.latitude),
                        lng: Number(values.longitude),
                      }
                    : currentLocation
                      ? {
                          lat: Number(currentLocation.lat),
                          lng: Number(currentLocation.lng),
                        }
                      : DEFAULT_MAP_CENTER
                }
                locationLoading={locationLoading}
                onLocationSelect={(lat, lng, address) => {
                  setFieldValue("latitude", lat);
                  setFieldValue("longitude", lng);
                  if (address) {
                    setFieldValue("locationLandmark", formatLocationNameInput(address));
                  }
                }}
              />

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="button"
                  style={cancelButtonStyle}
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button type="submit" style={saveButtonStyle} disabled={isSaving}>
                  {isSaving ? (
                    <l-bouncy size="22" speed="1.75" color="white"></l-bouncy>
                  ) : (
                    isEditMode ? "Update" : "Save"
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditRfid;
